import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BuildIcon from "@material-ui/icons/Build";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import AssignmentIcon from "@material-ui/icons/Assignment";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, overflow: "hidden", height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)", transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 30, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: { "&:hover": { backgroundColor: "#fffaf0" }, "&:nth-of-type(even)": { backgroundColor: "#fafafa" } },
}));

const PERIOD_OPTIONS = [
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Últimos 90 dias", value: 90 },
  { label: "Personalizado", value: "custom" },
];

const fmtCurrency = (val) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val || 0);
const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => { try { return format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR }); } catch { return d; } };
const fmtDateTime = (d) => { try { return format(new Date(d), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return "—"; } };

const STATUS_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "Abertas", value: "aberta" },
  { label: "Em Andamento", value: "em_andamento" },
  { label: "Aguardando Peça", value: "aguardando_peca" },
  { label: "Concluídas", value: "concluida" },
  { label: "Canceladas", value: "cancelada" },
];

const getStatusConfig = (status) => {
  const s = (status || "").toLowerCase().replace(" ", "_");
  const map = {
    aberta:           { label: "Aberta",           bg: "#ebf8ff", color: "#2b6cb0",  pieColor: "#4299e1" },
    em_andamento:     { label: "Em Andamento",      bg: "#fff3e5", color: "#c05621",  pieColor: "#ed8936" },
    aguardando_peca:  { label: "Aguard. Peça",      bg: "#fffff0", color: "#744210",  pieColor: "#d69e2e" },
    concluida:        { label: "Concluída",          bg: "#f0fff4", color: "#276749",  pieColor: "#38a169" },
    cancelada:        { label: "Cancelada",          bg: "#fff5f5", color: "#c53030",  pieColor: "#e53e3e" },
  };
  return map[s] || { label: status || "—", bg: "#f7fafc", color: "#718096", pieColor: "#a0aec0" };
};

export default function RelatorioOrdensServico() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(30);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 30)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [ordens, setOrdens] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const getDateRange = () => {
    if (period === "custom") return { start: customStart, end: customEnd };
    return { start: fmt(subDays(new Date(), Number(period))), end: fmt(new Date()) };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = { dateStart: start, dateEnd: end, pageSize: 300 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/service-orders", { params });
      setOrdens(Array.isArray(data) ? data : data?.serviceOrders || data?.data || []);
    } catch {
      setOrdens([]);
    }
    setLoading(false);
  }, [period, customStart, customEnd, statusFilter]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period, statusFilter]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = ordens
    .filter(o => {
      if (!search) return true;
      const q = search.toLowerCase();
      return String(o.id).includes(q) || (o.customer?.name || o.observacoesCliente || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va = a[sortBy] ?? "", vb = b[sortBy] ?? "";
      if (sortBy === "total" || sortBy === "subtotal") { va = Number(va); vb = Number(vb); }
      else if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const total = ordens.length;
  const concluidas = ordens.filter(o => (o.status || "").toLowerCase().includes("concluida") || (o.status || "").toLowerCase().includes("concluída")).length;
  const abertas = ordens.filter(o => (o.status || "").toLowerCase() === "aberta").length;
  const faturamento = ordens.reduce((s, o) => s + Number(o.total || 0), 0);
  const faturamentoConcluidas = ordens
    .filter(o => (o.status || "").toLowerCase().includes("concluida") || (o.status || "").toLowerCase().includes("concluída"))
    .reduce((s, o) => s + Number(o.total || 0), 0);
  const ticketMedio = concluidas > 0 ? faturamentoConcluidas / concluidas : 0;

  const allStatuses = [...new Set(ordens.map(o => o.status).filter(Boolean))];
  const statusCounts = allStatuses.reduce((acc, k) => {
    acc[k] = ordens.filter(o => o.status === k).length;
    return acc;
  }, {});

  const pieData = Object.entries(statusCounts)
    .map(([k, v]) => {
      const sc = getStatusConfig(k);
      return { name: sc.label, value: v, color: sc.pieColor };
    })
    .filter(d => d.value > 0);

  const barData = [
    { name: "Total", value: total, color: "#4299e1" },
    { name: "Concluídas", value: concluidas, color: "#38a169" },
    { name: "Abertas", value: abertas, color: "#ed8936" },
  ].filter(d => d.value > 0);

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "";
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((o, i) => {
      const sc = getStatusConfig(o.status);
      return `<tr>
        <td>#${o.id}</td>
        <td>${o.customer?.name || "—"}</td>
        <td><span style="padding:2px 10px;border-radius:12px;background:${sc.bg};color:${sc.color};font-weight:600">${sc.label}</span></td>
        <td>${fmtCurrency(o.subtotal)}</td>
        <td>${fmtCurrency(o.descontos)}</td>
        <td style="font-weight:700">${fmtCurrency(o.total)}</td>
        <td>${o.entregaPrevista ? fmtDisplay(o.entregaPrevista.toString().slice(0, 10)) : "—"}</td>
        <td>${o.createdAt ? fmtDateTime(o.createdAt) : "—"}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório OS</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#ed8936;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>🔧 Relatório de Ordens de Serviço</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<table><thead><tr><th>OS</th><th>Cliente</th><th>Status</th><th>Subtotal</th><th>Desconto</th><th>Total</th><th>Entrega Prev.</th><th>Criada em</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">${total} ordens · Faturamento total: ${fmtCurrency(faturamento)}</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-os-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Ordens de Serviço</Typography>
          <Typography className={classes.subtitle}>Relatório de OS abertas, em andamento e concluídas</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !filtered.length}
            style={{ backgroundColor: "#ed8936", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#ed8936", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        <TextField placeholder="Buscar OS ou cliente..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ flex: "1 1 160px" }} />
        {loading && <CircularProgress size={20} />}
        {!loading && ordens.length > 0 && (
          <Chip label={`${total} ordens · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#fff3e5", color: "#c05621", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        {[
          { label: "Total de OS", value: total, sub: "Todas as ordens", bg: "linear-gradient(135deg,#ebf8ff,#bee3f8)", border: "#4299e1", textColor: "#2b6cb0", Icon: AssignmentIcon },
          { label: "Concluídas", value: concluidas, sub: `${total > 0 ? Math.round(concluidas/total*100) : 0}% do total`, bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38a169", textColor: "#276749", Icon: CheckCircleIcon },
          { label: "Abertas", value: abertas, sub: "Aguardando atendimento", bg: "linear-gradient(135deg,#fff3e5,#fbd38d)", border: "#ed8936", textColor: "#c05621", Icon: HourglassEmptyIcon },
          { label: "Faturamento Total", value: fmtCurrency(faturamento), sub: "Soma de todas as OS", bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38b2ac", textColor: "#234e52", Icon: AttachMoneyIcon },
          { label: "Faturado (Concluídas)", value: fmtCurrency(faturamentoConcluidas), sub: "Receita realizada", bg: "linear-gradient(135deg,#fffff0,#fefcbf)", border: "#d69e2e", textColor: "#744210", Icon: AttachMoneyIcon },
          { label: "Ticket Médio", value: fmtCurrency(ticketMedio), sub: "Por OS concluída", bg: "linear-gradient(135deg,#faf5ff,#e9d8fd)", border: "#9f7aea", textColor: "#6b46c1", Icon: BuildIcon },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card className={classes.metricCard} elevation={0}>
              <Box className={classes.metricInner} style={{ background: m.bg, borderTop: `3px solid ${m.border}` }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography className={classes.metricLabel} style={{ color: m.textColor }}>{m.label}</Typography>
                  <m.Icon style={{ color: m.border, fontSize: 22, opacity: 0.8 }} />
                </Box>
                <Typography className={classes.metricValue} style={{ color: m.textColor, fontSize: String(m.value).length > 10 ? 20 : 30 }}>
                  {loading ? "—" : m.value}
                </Typography>
                <Typography className={classes.metricSub} style={{ color: m.textColor }}>{m.sub}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>OS por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [`${val} OS`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Comparativo Geral</Typography>
            {loading || barData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }} barSize={50}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [`${val} OS`, "Quantidade"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Lista de Ordens ({filtered.length})</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>OS</TableCell>
                <TableCell className={classes.tableHeadCell}>Cliente</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
                <TableCell className={classes.tableHeadCell} align="right">Subtotal</TableCell>
                <TableCell className={classes.tableHeadCell} align="right">Desconto</TableCell>
                <TableCell className={classes.tableHeadCell} align="right">
                  <TableSortLabel active={sortBy === "total"} direction={sortBy === "total" ? sortDir : "desc"}
                    onClick={() => handleSort("total")} style={{ color: "#fff" }}>
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Entrega Prev.</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "createdAt"} direction={sortBy === "createdAt" ? sortDir : "desc"}
                    onClick={() => handleSort("createdAt")} style={{ color: "#fff" }}>
                    Criada
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" style={{ padding: 40 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" style={{ padding: 40, color: "#a0aec0" }}>Nenhuma ordem encontrada</TableCell></TableRow>
              ) : filtered.map((o) => {
                const sc = getStatusConfig(o.status);
                return (
                  <TableRow key={o.id} className={classes.tableRow}>
                    <TableCell>
                      <Chip label={`#${o.id}`} size="small"
                        style={{ backgroundColor: "#fff3e5", color: "#c05621", fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{o.customer?.name || "—"}</Typography>
                      {o.observacoesCliente && (
                        <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{o.observacoesCliente.slice(0, 40)}…</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sc.label} size="small"
                        style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, minWidth: 90 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontSize: 13, color: "#718096" }}>{fmtCurrency(o.subtotal)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontSize: 13, color: "#c53030" }}>
                        {Number(o.descontos || 0) > 0 ? `-${fmtCurrency(o.descontos)}` : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 700, fontSize: 14, color: "#276749" }}>{fmtCurrency(o.total)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>
                        {o.entregaPrevista ? fmtDisplay(o.entregaPrevista.toString().slice(0, 10)) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>
                        {o.createdAt ? fmtDateTime(o.createdAt) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
